import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, dateFmt, downloadFile } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { PageHeader, EmptyState, Th, Td, NumberInput, SortTh, useSortable } from "@/components/common";
import { ListFilter, useListFilter } from "@/components/common/ListFilter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, Plus, Lock, CheckCircle2, Trash2, FileText, FileSpreadsheet, Search } from "lucide-react";
import { cn } from "@/lib/utils";

export default function Counts() {
  const { user } = useAuth();
  const { t, tp } = useI18n();
  const nav = useNavigate();
  const canEdit = can(user, "counts:edit");
  const [rows, setRows] = useState(null);
  const [inv, setInv] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [open, setOpen] = useState(false);
  const [counted, setCounted] = useState({});
  const [note, setNote] = useState("");
  const [lineEdits, setLineEdits] = useState({});
  const [dq, setDq] = useState("");
  // Sortierbar sind nur die STATISCHEN Spalten (SKU, Systembestand). „Gezählt" bleibt bewusst
  // unsortierbar, damit Zeilen beim Eintippen der Mengen nicht unter dem Cursor wegspringen.
  const invACC = useMemo(() => ({ sku: (v) => v.sku || "", physical: (v) => v.physical || 0 }), []);
  const invFiltered = useMemo(() => {
    const n = dq.trim().toLowerCase();
    return n ? inv.filter((v) => (v.sku || "").toLowerCase().includes(n)) : inv;
  }, [inv, dq]);
  const { sorted: invSorted, sortKey: invKey, sortDir: invDir, toggle: invToggle } = useSortable(invFiltered, invACC);
  const isp = (k) => ({ sortId: k, active: invKey === k, dir: invDir, onSort: invToggle });
  const saveLine = async (cid, variantId, val) => {
    const q = Number(val); if (isNaN(q)) { toast.error(t("cnt.invalidQty")); return; }
    try { await api.put(`/inventory-counts/${cid}/lines/${variantId}`, { counted_qty: q }); toast.success(t("cnt.qtyCorrected")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const load = () => {
    api.get("/inventory-counts").then((r) => setRows(r.data)).catch(() => setRows([]));
    api.get("/inventory").then((r) => setInv(r.data)).catch(() => {});
    api.get("/warehouses").then((r) => setWarehouses(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setDq(""); setCounted(Object.fromEntries(inv.map((v) => [v.variant_id, v.physical]))); setOpen(true); };

  const save = async () => {
    try {
      await api.post("/inventory-counts", { warehouse_id: warehouses[0]?.id, note,
        lines: inv.filter((v) => counted[v.variant_id] !== "" && counted[v.variant_id] != null)
          .map((v) => ({ variant_id: v.variant_id, counted_qty: Number(counted[v.variant_id]) })) });
      toast.success(t("cnt.draftSaved")); setOpen(false); setNote(""); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const finalize = async (c) => {
    if (!window.confirm(t("cnt.finalizeConfirm"))) return;
    try { await api.post(`/inventory-counts/${c.id}/finalize`); toast.success(t("cnt.finalized")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const discard = async (c) => {
    if (!window.confirm(t("cnt.discardConfirm"))) return;
    try { await api.delete(`/inventory-counts/${c.id}`); toast.success(t("cnt.discarded")); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const exportXlsx = (c) => downloadFile(`/inventory-counts/${c.id}/export.xlsx`, `inventur_${c.id.slice(0, 8)}.xlsx`).catch((e) => toast.error(apiError(e)));

  const filter = useListFilter(rows, { fields: ["created_by_email", "note", "status"], dateField: "created_at", statusField: "status", storageKey: "counts" });

  return (
    <div>
      <PageHeader title={t("cnt.title")} subtitle={t("cnt.subtitle")}
        actions={canEdit && <Button data-testid="new-count-btn" onClick={openNew}><Plus className="h-4 w-4 mr-1.5" /> {t("cnt.new")}</Button>} />

      <ListFilter filter={filter} testId="counts" withDates withStatus placeholder={t("filter.searchName")}
        statusLabel={(s) => (s === "final" ? t("cnt.final") : t("cnt.draft"))}
        exportName="inventuren" exportColumns={[
          { label: t("common.date"), value: (c) => dateFmt(c.created_at) },
          { label: t("common.person"), value: (c) => c.created_by_email || "" },
          { label: t("common.status"), value: (c) => (c.status === "final" ? t("cnt.final") : t("cnt.draft")) },
          { label: t("common.positions"), value: (c) => c.lines.length },
          { label: t("cnt.difference"), value: (c) => c.lines.filter((l) => l.difference !== 0).length },
          { label: t("cnt.finalizedAt"), value: (c) => (c.finalized_at ? dateFmt(c.finalized_at) : "") },
        ]} />

      {rows === null ? <div className="text-muted-foreground">{t("common.loading")}</div> :
        rows.length === 0 ? <EmptyState testId="counts-empty" icon={ClipboardList} title={t("cnt.emptyTitle")} hint={t("cnt.emptyHint")} /> : (
          <div className="space-y-3" data-testid="counts-list">
            {filter.filtered.map((c) => { const isFinal = c.status === "final"; return (
              <div key={c.id} className="rounded-md border border-border bg-card p-4 shadow-sm" data-testid={`count-card-${c.id.slice(0, 8)}`}>
                <div className="flex flex-wrap justify-between items-start gap-2 mb-2">
                  <div>
                    <div className="text-sm font-medium flex items-center gap-2">
                      {dateFmt(c.created_at)} · {c.created_by_email}
                      {isFinal
                        ? <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary text-[10px] px-2 py-0.5 font-semibold" data-testid={`count-status-${c.id.slice(0, 8)}`}><Lock className="h-3 w-3" /> {t("cnt.final")}</span>
                        : <span className="inline-flex items-center gap-1 rounded-full bg-chart-3/15 text-chart-3 text-[10px] px-2 py-0.5 font-semibold" data-testid={`count-status-${c.id.slice(0, 8)}`}>{t("cnt.draft")}</span>}
                    </div>
                    {isFinal && <div className="text-[11px] text-muted-foreground">{t("cnt.finalizedAt")}: {dateFmt(c.finalized_at)} · {c.finalized_by}</div>}
                    <div className="text-xs text-muted-foreground">{tp("plural.lines", c.lines.length)} · {tp("plural.differences", c.lines.filter((l) => l.difference !== 0).length)}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => nav(`/inventur/${c.id}/report`)} data-testid={`count-report-${c.id.slice(0, 8)}`}><FileText className="h-3.5 w-3.5 mr-1" /> {t("cnt.report")}</Button>
                    <Button size="sm" variant="outline" onClick={() => exportXlsx(c)} data-testid={`count-xlsx-${c.id.slice(0, 8)}`}><FileSpreadsheet className="h-3.5 w-3.5 mr-1" /> Excel</Button>
                    {canEdit && !isFinal && <>
                      <Button size="sm" onClick={() => finalize(c)} data-testid={`count-finalize-${c.id.slice(0, 8)}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" /> {t("cnt.finalize")}</Button>
                      <Button size="sm" variant="ghost" onClick={() => discard(c)} data-testid={`count-discard-${c.id.slice(0, 8)}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                    </>}
                  </div>
                </div>
                <table className="w-full"><thead><tr><Th>{t("cnt.item")}</Th><Th right>{t("cnt.systemQty")}</Th><Th right>{t("cnt.countedQty")}</Th><Th right>{t("cnt.difference")}</Th></tr></thead>
                  <tbody>{(isFinal ? c.lines.filter((l) => l.difference !== 0) : c.lines).map((l, i) => { const key = `${c.id}:${l.variant_id}`; return (
                    <tr key={i}><Td className="text-xs"><span className="font-mono">{l.sku || l.variant_id.slice(0, 8)}</span>{l.product_name ? ` · ${l.product_name}` : ""}</Td><Td right mono>{l.system_qty}</Td>
                      <Td right>{isFinal ? <span className="font-mono">{l.counted_qty}</span> : (
                        <span className="inline-flex items-center gap-1 justify-end">
                          <NumberInput value={lineEdits[key] ?? l.counted_qty} onChange={(v) => setLineEdits({ ...lineEdits, [key]: v })} className="w-20 h-8 text-right" testId={`ist-${l.variant_id}`} />
                          <Button size="sm" variant="ghost" className="h-8 px-2" onClick={() => saveLine(c.id, l.variant_id, lineEdits[key] ?? l.counted_qty)} data-testid={`ist-save-${l.variant_id}`}>OK</Button>
                        </span>)}</Td>
                      <Td right mono className={cn(l.difference < 0 ? "text-destructive" : "text-primary")}>{l.difference > 0 ? "+" : ""}{l.difference}</Td></tr>); })}
                    {isFinal && c.lines.every((l) => l.difference === 0) && <tr><Td className="text-muted-foreground text-sm">{t("cnt.noDifferences")}</Td></tr>}
                  </tbody></table>
              </div>); })}
          </div>
        )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t("cnt.new")}{warehouses[0]?.name ? ` · ${warehouses[0].name}` : ""}</DialogTitle></DialogHeader>
          <div><Label>{t("common.note")}</Label><Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" data-testid="count-note" /></div>
          <div className="relative mt-2">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input value={dq} onChange={(e) => setDq(e.target.value)} data-testid="count-search"
              placeholder={t("cnt.searchPh")}
              className="h-9 w-full rounded-md border border-input bg-background pl-8 pr-3 text-sm outline-none focus:ring-2 focus:ring-ring" />
          </div>
          <div className="text-xs text-muted-foreground" data-testid="count-search-info">
            {dq.trim()
              ? t("cnt.itemsFiltered", { shown: invSorted.length, total: inv.length })
              : tp("plural.items", inv.length)}
          </div>
          <div className="max-h-80 overflow-y-auto">
            <table className="w-full"><thead className="sticky top-0 bg-card"><tr>
              <SortTh {...isp("sku")}>SKU</SortTh>
              <SortTh right {...isp("physical")}>{t("cnt.system")}</SortTh>
              <Th right>{t("cnt.counted")}</Th></tr></thead>
              <tbody data-testid="count-inv-table">{invSorted.length === 0 && (
                <tr><Td colSpan={3} className="text-muted-foreground" data-testid="count-no-match">{t("common.noMatchFor", { q: dq })}</Td></tr>
              )}{invSorted.map((v) => (
                <tr key={v.variant_id}><Td mono>{v.sku}</Td><Td right mono>{v.physical}</Td>
                  <Td right><Input type="number" className="w-24 h-8 ml-auto" value={counted[v.variant_id] ?? ""} onChange={(e) => setCounted({ ...counted, [v.variant_id]: e.target.value })} data-testid={`count-${v.sku}`} /></Td></tr>))}
              </tbody></table>
          </div>
          <DialogFooter><Button data-testid="save-count-btn" onClick={save}>{t("cnt.saveDraft")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
